// "Modular by design" section — replaces fixed pricing tiers. Kanriva™ is
// sold per module: a customer can run it all-in-one or activate only the
// modules they need, then scale up anytime. Pricing is agreed directly
// with Kaizen Kanri after the free trial (full access for 14 days).
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { WALKTHROUGH_MAILTO } from '../../i18n/trust';
import {
  Sparkles, ArrowRight, MailIcon, Rocket, SlidersHorizontal, TrendingUp,
  Briefcase, Workflow, CalendarDays, Users, MessageSquare,
} from 'lucide-react';

// Two-level catalog mirrored from the app's platform module catalog so
// the marketing story matches exactly what a Master Admin can grant.
const MODULE_GROUPS = [
  {
    key: 'projects', label: 'Projects', icon: Briefcase,
    blurb: 'Continuous-improvement toolkit',
    features: ['DMAIC / Kaizen', '6S Audits', 'Value Stream / Process Flow', 'KPIs, Goals & OKRs'],
  },
  {
    key: 'workfloor', label: 'Workfloor', icon: Workflow,
    blurb: 'Day-to-day warehouse operations',
    features: ['Expedition', 'Dockplanning', 'Order Picking', 'Damage Log', 'Checklists & Audits'],
  },
  {
    key: 'planning', label: 'Planning', icon: CalendarDays,
    blurb: 'Workforce & shift planning',
    features: ['Multi-site & multi-department planning', 'Workload-driven hours', 'Explainable staffing suggestions', 'Absence & holiday overlays'],
  },
  {
    key: 'teams', label: 'Teams', icon: Users,
    blurb: 'People & behaviour',
    features: ['Team management', 'Presence & documented follow-up', 'Roles & permissions'],
  },
  {
    key: 'messages', label: 'Messages', icon: MessageSquare,
    blurb: 'Stay in sync',
    features: ['Group chat', 'Real-time notifications', 'Shared context across modules'],
  },
];

const PRICE_BANDS = [
  { size: 'Up to 50 active workers', price: 'from €495' },
  { size: '51 – 150 active workers', price: 'from €795' },
  { size: '151 – 300 active workers', price: 'from €1,195' },
  { size: '301 – 600 active workers', price: 'from €1,795' },
  { size: 'More than 600 workers', price: 'tailored enterprise pricing' },
];

const STEPS = [
  { icon: Rocket, title: 'See it on your numbers', body: 'Book a 30-minute walkthrough, or open the demo tour first. Prefer to click around yourself? The 14-day trial unlocks every module — no card, no commitment.' },
  { icon: SlidersHorizontal, title: 'Keep what you need', body: 'Run it all-in-one, or activate just the modules that fit your operation. Mix and match across Projects, Workfloor and Planning.' },
  { icon: TrendingUp, title: 'Agree a price & scale', body: 'When you know what you want, we agree a price with Kaizen Kanri based on your module mix — and you add more anytime.' },
];

export const PricingSection = () => {
  return (
    <section id="pricing" className="relative py-24 bg-[#0a0a0a] text-white" data-testid="modular-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#00CED1]/40 bg-[#00CED1]/10 text-[#00CED1] text-xs font-semibold mb-4">
            <Sparkles className="h-3 w-3" /> Modular by design
          </div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight">One platform. Only the modules you need.</h2>
          <p className="mt-4 text-base text-white/60 leading-relaxed">
            Kanriva™ is fully modular. Run it as an all-in-one operations suite, or switch on just the parts that
            matter to you — say, <span className="text-white/85">Projects + Planning</span> today, and add
            <span className="text-white/85"> Workfloor</span> when you grow. You're always in control, and so are we:
            access is granted per tenant, per module.
          </p>
        </div>

        {/* How it works */}
        <div className="grid sm:grid-cols-3 gap-4 mb-14" data-testid="modular-steps">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.title} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 relative">
                <span className="absolute top-5 right-5 font-display text-3xl text-white/10">{i + 1}</span>
                <div className="h-10 w-10 rounded-xl bg-[#00CED1]/12 border border-[#00CED1]/25 flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5 text-[#00CED1]" />
                </div>
                <h3 className="font-display text-lg tracking-tight mb-1.5">{s.title}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{s.body}</p>
              </div>
            );
          })}
        </div>

        {/* Indicative platform pricing */}
        <div className="mb-14" data-testid="price-bands">
          <div className="flex items-center gap-2 mb-5 justify-center">
            <span className="h-1 w-8 bg-[#00CED1] rounded-full" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/50">Indicative platform pricing</span>
            <span className="h-1 w-8 bg-[#00CED1] rounded-full" />
          </div>
          <p className="text-sm text-white/55 leading-relaxed text-center max-w-3xl mx-auto mb-7">
            So you know which budget conversation this is. Prices are per site, per month, and scale with the number of
            active workers in the workspace. Each band gives that number of workers and users full access to the platform,
            unlimited FlowCrew™ mobile access for your crew, updates and support — implementation and pilot scopes are
            quoted separately. Need only part of the platform? Leaving modules out brings the figure down accordingly;
            that is where the conversation starts, and your final price follows the module mix you actually use.
          </p>
          <div className="overflow-x-auto rounded-2xl border border-white/10">
            <table className="w-full text-sm min-w-[520px]">
              <thead>
                <tr className="bg-white/[0.04] text-white/45 text-xs uppercase tracking-wider">
                  <th className="text-left font-semibold px-5 py-3">Active workers on site</th>
                  <th className="text-right font-semibold px-5 py-3">Platform, per month</th>
                </tr>
              </thead>
              <tbody>
                {PRICE_BANDS.map((b) => (
                  <tr key={b.size} className="border-t border-white/[0.06]">
                    <td className="px-5 py-3.5 text-white/70">{b.size}</td>
                    <td className="px-5 py-3.5 text-right font-semibold text-[#00CED1] whitespace-nowrap">{b.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-white/35 text-center mt-4">
            Annual agreements and pilot scopes are quoted individually. No card is needed for the trial and nothing auto-renews.
          </p>
        </div>

        {/* Module catalog */}
        <div className="mb-12">
          <div className="flex items-center gap-2 mb-5 justify-center">
            <span className="h-1 w-8 bg-[#00CED1] rounded-full" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/50">Pick your modules</span>
            <span className="h-1 w-8 bg-[#00CED1] rounded-full" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {MODULE_GROUPS.map((g) => {
              const Icon = g.icon;
              return (
                <div
                  key={g.key}
                  className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:border-[#00CED1]/30 transition-colors"
                  data-testid={`modular-card-${g.key}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-9 w-9 rounded-lg bg-[#00CED1]/10 border border-[#00CED1]/20 flex items-center justify-center">
                      <Icon className="h-4 w-4 text-[#00CED1]" />
                    </div>
                    <div>
                      <div className="font-display text-base tracking-tight leading-none">{g.label}</div>
                      <div className="text-[11px] text-white/40 mt-0.5">{g.blurb}</div>
                    </div>
                  </div>
                  <ul className="space-y-1.5 text-sm text-white/65">
                    {g.features.map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <span className="h-1 w-1 mt-2 rounded-full bg-[#00CED1] shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
            {/* Always-included card */}
            <div className="rounded-2xl border border-dashed border-white/15 bg-transparent p-6 flex flex-col justify-center" data-testid="modular-card-core">
              <div className="font-display text-base tracking-tight mb-1.5">Always included</div>
              <p className="text-sm text-white/55 leading-relaxed">
                Dashboard, Archive, analytics and exports come with every workspace — whatever module mix you choose.
              </p>
            </div>
          </div>
        </div>

        {/* Trial + contact CTA */}
        <div className="rounded-2xl border border-white/10 bg-gradient-to-r from-[#00CED1]/10 to-transparent p-6 sm:p-8 flex flex-wrap items-center justify-between gap-6" data-testid="modular-cta-strip">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 text-[#00CED1] mb-1">
              <Sparkles className="h-4 w-4" />
              <span className="text-xs uppercase tracking-wider font-semibold">Free 14-day trial — every module unlocked</span>
            </div>
            <p className="text-white/70 text-sm leading-relaxed">
              Try the whole platform with full access during your trial. Decide what you actually use, then talk to
              Kaizen Kanri about a price that fits your module mix. No card required, no auto-renew.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto sm:shrink-0">
            <Button asChild className="w-full sm:w-auto bg-[#00CED1] hover:bg-[#00b4b7] text-[#0a0a0a] font-semibold" data-testid="modular-walkthrough-cta">
              <a href={WALKTHROUGH_MAILTO}>Book a walkthrough <ArrowRight className="h-4 w-4 ml-2" /></a>
            </Button>
            <Button asChild variant="outline" className="w-full sm:w-auto border-[#00CED1]/40 bg-transparent text-[#00CED1] hover:bg-[#00CED1]/10" data-testid="modular-trial-cta">
              <Link to="/signup">Start free trial</Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="w-full sm:w-auto max-w-full border-white/20 text-white hover:bg-white/10 bg-transparent"
              data-testid="modular-contact-cta"
            >
              <a href="mailto:info@kaizenkanri.nl?subject=Kanriva™%20—%20tailored%20module%20mix&body=Hi%2C%0A%0AWe%27d%20like%20to%20discuss%20a%20Kanriva™%20setup%20for%20our%20operation.%0A%0ACompany%20name%3A%0AModules%20we%27re%20interested%20in%3A%0AApprox.%20headcount%3A%0A%0AThanks%21">
                <MailIcon className="h-4 w-4 mr-2" /> Contact us for a tailored mix
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
